import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import {
  actionEnum,
  AssignMember,
  AssignMemberDTO,
  AssignMembers,
  AssignMembersDTO,
  AuditLogsDto,
  AccountRequestDto,
  AwsAccountId,
  AwsAccountProjectDto,
  AwsAccountUser,
  BlueBoxAwsAccountDto,
  BlueBoxValidateSecretDto,
  BlueBoxValidateSecretResponseDto,
  HeliosCoreKmsRoleCheckDto,
  HeliosCoreKmsRoleCheckResponseDto,
  CreateAccess,
  CreateTeamsDto,
  DeleteAccess,
  InstallCatalogPluginDto,
  InstalledPluginDto,
  InstalledPluginsDTO,
  LeanIxTeamDto,
  PluginCheckLogDto,
  PluginsAndPlayListsDto,
  ProjectInfo,
  RemoveTeamDTO,
  SIPCentralApi,
  TeamId,
  TeamsDto,
  ToolDto,
  TransitService,
  UpdateAccess,
  UserDto,
  UserId,
  WiwId,
  WiwIdDto,
  AccountRequestConfig,
  Regions,
  FirewallDomains,
  MattermostValidateUsernameDto,
  CatalogPlaylistDto,
  CreatePlaylistExecutionDto,
  PlaylistExecutionConfigDto,
  PlaylistExecutionDto,
  PlaylistResumeRequestDto,
  PlaylistUninstallRequestDto,
  SavePluginConfigDraftRequestDto,
  PluginConfigDraftResponseDto,
} from '@mercedes-benz/sip-central-common';

export const sipCentralApiRef: ApiRef<SIPCentralApi> =
  createApiRef<SIPCentralApi>({
    id: 'plugin.sip-central.api',
  });

export type SipCentralRequest = {
  method: string;
  path: string;
  body?: object;
  responseAsJson?: boolean;
  bearerToken?: string;
};

export class SIPCentralApiClient implements SIPCentralApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi?: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch };
  }

  getSIPContent(): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/sip/content`,
    });
  }

  getAccountMembers({ userId }: UserId): Promise<AwsAccountUser[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/users/${encodeURIComponent(userId)}/account-members`,
    });
  }

  getUser({ userId }: UserId): Promise<UserDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/users/${encodeURIComponent(userId)}`,
    });
  }

  getUserForCatalog({ userId }: UserId): Promise<UserDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/user/${encodeURIComponent(userId)}`,
    });
  }

  validateWiwId(wiwId: WiwId): Promise<WiwIdDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/alice/users/${wiwId}`,
    });
  }

  async createTeams(body: CreateTeamsDto): Promise<any> {
    const res = await this.submitRequest({
      method: 'POST',
      path: `/teams`,
      body,
    });

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.CREATE_TEAM,
      activities: [
        {
          teamDetails: body.teams.map(team => ({
            currentTeam: {
              name: team.name,
              owner: team.owner,
              manager: team.manager,
            },
          })),
        },
      ],
      actionDate: new Date().toJSON(),
    });

    return res;
  }

  async updateTeams(body: TeamsDto): Promise<TeamsDto> {
    const res = await this.submitRequest({
      method: 'PUT',
      path: `/teams`,
      body,
    });
    if (
      body.teams.filter(
        team => team.oldTeam !== team.name || team.oldManager !== team.manager,
      ).length > 0
    ) {
      this.auditLogs({
        currentUser: undefined,
        action: actionEnum.UPDATE_TEAM,
        activities: [
          {
            teamDetails: body.teams
              .filter(
                team =>
                  team.oldTeam !== team.name ||
                  team.oldManager !== team.manager,
              )
              .map(team => ({
                currentTeam: {
                  name: team.oldTeam,
                  owner: team.owner,
                  manager: team.oldManager,
                },
                updatedTeam: {
                  name: team.name,
                  manager: team.manager,
                },
              })),
          },
        ],
        actionDate: new Date().toJSON(),
      });
    }
    return res;
  }

  async assignMembers(body: AssignMembers): Promise<AssignMembersDTO> {
    const res = await this.submitRequest({
      method: 'POST',
      path: `/teams/members`,
      body,
    });

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.ADD_TEAM_MEMBERS,
      activities: body.members.map(member => ({
        performedOn: member.id,
        teamMembers: [
          {
            teamRole: member.teamRole,
            teams: member.teamNames,
          },
        ],
      })),
      actionDate: new Date().toJSON(),
    });

    return res;
  }

  async deleteTeam(
    teamId: string,
    lastTeam?: Boolean,
    teamName?: string,
  ): Promise<RemoveTeamDTO> {
    const path = `/teams/${encodeURIComponent(teamId)}${
      lastTeam ? '?lastTeam=true' : ''
    }`;
    const methodBody = {
      method: 'DELETE',
      path: path,
    };
    const res = this.submitRequest(methodBody);

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.DELETE_TEAM,
      activities: [{ teamDetails: [{ currentTeam: { name: teamName } }] }],
      actionDate: new Date().toJSON(),
      currentTeam: teamName,
    });

    return res;
  }

  async reassignTeamMembers(
    teamId: string,
    body?: AssignMembers,
  ): Promise<RemoveTeamDTO> {
    const path = `/teams/${encodeURIComponent(teamId)}/reassign`;
    const methodBody = {
      method: 'POST',
      path: path,
    };

    if (body !== undefined) {
      const res = await this.submitRequest({ ...methodBody, body });
      this.auditLogs({
        currentUser: undefined,
        action: actionEnum.REASSIGN_USERS,
        activities: body.members
          .filter((member: any) => member.reAssignedTeamNames?.length !== 0)
          .map(member => ({
            performedOn: member.id,
            teamMembers: [{ teams: member.reAssignedTeamNames }],
          })),
        actionDate: new Date().toJSON(),
        currentTeam: body.teamName,
      });
      return res;
    }
    return this.submitRequest(methodBody);
  }

  async assignMember(
    body: AssignMember,
    { teamId }: TeamId,
    teamName?: string,
  ): Promise<AssignMemberDTO> {
    const res = await this.submitRequest({
      method: 'POST',
      path: `/teams/${encodeURIComponent(teamId)}/member`,
      body,
    });

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.ADD_USER,
      activities: [
        {
          performedOn: body.id,
          teamMembers: [{ teamRole: body.teamRole }],
        },
      ],
      actionDate: new Date().toJSON(),
      currentTeam: teamName,
    });

    return res;
  }

  async createAccess(body: CreateAccess): Promise<any> {
    const res = await this.submitRequest({
      method: 'POST',
      path: `/users`,
      body,
    });

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.GRANT_PERMISSION,
      activities: [
        {
          performedOn: body.id,
          toolPermissions: {
            tool: 'AWS',
            permissions: body.awsRoleRequests.map(ele => ({
              currentRoles: ele.awsSelectedRoles,
              instance: ele.awsInstance,
            })),
          },
        },
      ],
      actionDate: new Date().toJSON(),
    });

    return res;
  }

  getTeams({ userId }: UserId): Promise<TeamsDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/users/${encodeURIComponent(userId)}/teams`,
    });
  }

  getTeam({ teamId }: TeamId, enhanceUserInfo?: Boolean): Promise<any> {
    const queryParm = !!enhanceUserInfo
      ? `?enhanceUserInfo=${enhanceUserInfo}`
      : '';
    return this.submitRequest({
      method: 'GET',
      path: `/teams/${encodeURIComponent(teamId)}${queryParm}`,
    });
  }

  async removeMember({ userId }: UserId, ownerId: string): Promise<any> {
    const res = this.submitRequest({
      method: 'DELETE',
      path: `/teams/users/${encodeURIComponent(
        userId,
      )}?ownerId=${encodeURIComponent(ownerId)}`,
    });

    await this.auditLogs({
      currentUser: undefined,
      action: actionEnum.DELETE_USER,
      activities: [{ performedOn: userId }],
      actionDate: new Date().toJSON(),
    });

    return res;
  }

  async updateAccess(body: UpdateAccess, { userId }: UserId): Promise<any> {
    const res = this.submitRequest({
      method: 'PUT',
      path: `/users/${encodeURIComponent(userId)}`,
      body,
    });
    if (
      body.awsRoleRequests.filter(
        awsRoleRequest => awsRoleRequest.awsExistingRoles?.length !== 0,
      ).length > 0
    ) {
      await this.auditLogs({
        currentUser: undefined,
        action: actionEnum.UPDATE_PERMISSION,
        activities: [
          {
            performedOn: userId,
            toolPermissions: {
              tool: 'AWS',
              permissions: body.awsRoleRequests
                .filter(
                  awsRoleRequest =>
                    awsRoleRequest.awsExistingRoles?.length !== 0,
                )
                .map(ele => ({
                  currentRoles: ele.awsExistingRoles,
                  updatedRoles: ele.awsSelectedRoles,
                  instance: ele.awsInstance,
                })),
            },
          },
        ],
        actionDate: new Date().toJSON(),
      });
    }

    return res;
  }

  async deleteAccess(body: DeleteAccess, { userId }: UserId): Promise<any> {
    const res = await this.submitRequest({
      method: 'POST',
      path: `/users/${encodeURIComponent(userId)}/revoke`,
      body: { awsAccountIds: body.awsAccountIds?.map(ele => ele.awsAccountId) },
    });

    this.auditLogs({
      currentUser: undefined,
      action: actionEnum.REVOKE_PERMISSION,
      activities: [
        {
          performedOn: userId,
          toolPermissions: {
            tool: 'AWS',
            permissions: body.awsAccountIds.map(ele => ({
              instance: ele.awsInstance,
            })),
          },
        },
      ],
      actionDate: new Date().toJSON(),
    });

    return res;
  }

  getTeamsOfOwner({ userId }: UserId): Promise<TeamsDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/teams/${encodeURIComponent(userId)}/get-teams`,
    });
  }

  auditLogs(body: AuditLogsDto): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/audit-logs`,
      body,
    }).catch();
  }

  getPluginListAndPlayList(
    accountId: string,
    _userId: string,
    region?: string,
  ): Promise<PluginsAndPlayListsDto> {
    let path = `/catalog/plugins/${encodeURIComponent(accountId)}`;
    if (region) {
      path += `?region=${encodeURIComponent(region)}`;
    }
    return this.submitRequest({
      method: 'GET',
      path,
    });
  }

  getPluginLogs(buildId: string): Promise<PluginCheckLogDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin/logs/${encodeURIComponent(buildId)}`,
    });
  }

  getDryRunPluginLogs(buildId: string): Promise<PluginCheckLogDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin/dryrun-logs/${encodeURIComponent(buildId)}`,
    });
  }

  getPluginStatus(pluginRequestId: string): Promise<InstalledPluginDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin/status/${encodeURIComponent(pluginRequestId)}`,
    });
  }

  getInstalledPluginList(
    accountId: string,
    options?: { userId?: string; region?: string },
  ): Promise<InstalledPluginsDTO> {
    const region = options?.region;
    let queryString = '';
    if (region) {
      queryString = `?region=${encodeURIComponent(region)}`;
    }
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin-request/${encodeURIComponent(accountId)}${queryString}`,
    });
  }

  getPluginRequestHistory(
    pluginId: string,
    awsAccountId: string,
    region: string,
    startDate: string,
    endDate: string,
    _userId?: string,
    namespace?: string,
  ): Promise<{ pluginRequestHistory: any[] }> {
    const queryParams = [
      `pluginId=${encodeURIComponent(pluginId)}`,
      `awsAccountId=${encodeURIComponent(awsAccountId)}`,
      `region=${encodeURIComponent(region)}`,
      `startDate=${encodeURIComponent(startDate)}`,
      `endDate=${encodeURIComponent(endDate)}`,
    ];
    if (namespace) {
      queryParams.push(`namespace=${encodeURIComponent(namespace)}`);
    }
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin-request-history?${queryParams.join('&')}`,
    });
  }

  getDryRunPluginList(
    accountId: string,
    options?: { userId?: string; region?: string },
  ): Promise<InstalledPluginsDTO> {
    const region = options?.region;
    let queryString = '';
    if (region) {
      queryString = `?region=${encodeURIComponent(region)}`;
    }
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin/dry-run-requests/${encodeURIComponent(accountId)}${queryString}`,
    });
  }

  getTools(): Promise<ToolDto[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/tools`,
    });
  }

  getPluginConfig(pluginName: string): Promise<string> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin-config/${encodeURIComponent(pluginName)}`,
    });
  }

  getPluginConfigForVersion(
    pluginId: string,
    version: string,
  ): Promise<string> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin-config/${encodeURIComponent(pluginId)}/version?version=${encodeURIComponent(version)}`,
    });
  }

  getVersionList(pluginId: string, _userId?: string): Promise<string[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin-config/${encodeURIComponent(pluginId)}/versions`,
    });
  }

  getBlueBoxAccountDetails({
    awsAccountId,
  }: AwsAccountId): Promise<BlueBoxAwsAccountDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/aws-account/${encodeURIComponent(awsAccountId)}`,
    });
  }

  installCatalogPlugin(body: InstallCatalogPluginDto): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/install`,
      body,
    });
  }

  installCatalogPluginDryRun(body: InstallCatalogPluginDto): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/install-dryrun`,
      body,
    });
  }

  uninstallCatalogPlugin(
    pluginRequestId: string,
    _userId: string,
  ): Promise<any> {
    return this.submitRequest({
      method: 'DELETE',
      path: `/catalog/plugin/uninstall/${encodeURIComponent(pluginRequestId)}`,
    });
  }

  uninstallCatalogPluginDryRun(
    pluginRequestId: string,
    _userId: string,
  ): Promise<any> {
    return this.submitRequest({
      method: 'DELETE',
      path: `/catalog/plugin/uninstall-dryrun/${encodeURIComponent(pluginRequestId)}`,
    });
  }

  updateCatalogPlugin(
    eventId: string,
    buildStatus: string,
    body: InstallCatalogPluginDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/plugin/update-config/${encodeURIComponent(eventId)}
      ?buildStatus=${encodeURIComponent(buildStatus)}`,
      body,
    });
  }

  updateCatalogPluginDryRun(
    eventId: string,
    buildStatus: string,
    body: InstallCatalogPluginDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/plugin/update-config-dryrun/${encodeURIComponent(eventId)}?buildStatus=${encodeURIComponent(buildStatus)}`,
      body,
    });
  }

  upgradeCatalogPluginDryRun(
    pluginRequestId: string,
    body: InstallCatalogPluginDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/plugin/upgrade-dryrun/${encodeURIComponent(pluginRequestId)}`,
      body,
    });
  }

  hasSupportRole(userId: string): Promise<boolean> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/user/${encodeURIComponent(userId)}/has-support-role`,
    });
  }

  validateLeanIxId(leanIx: string): Promise<LeanIxTeamDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/leanix/${leanIx}`,
    });
  }

  getPluginRequestForNamespace(
    awsAccount: string,
    pluginName: string,
    namespace: string,
  ): Promise<InstalledPluginDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/namespace/${awsAccount}?plugin=${pluginName}&namespace=${namespace}`,
    });
  }

  onboardCloudEngineAccountDetails(
    accountDetails: BlueBoxAwsAccountDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/aws-account`,
      body: accountDetails,
    });
  }

  updateCloudEngineAccountDetails(
    accountId: string,
    accountDetails: BlueBoxAwsAccountDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/aws-account/${accountId}`,
      body: accountDetails,
    });
  }

  getPluginRepos(_currentUser: string): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/versioning/repos`,
    });
  }

  getBetaBranches(repoId: string): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/versioning/get-branches?repoId=${encodeURIComponent(repoId)}`,
    });
  }

  getAvailableNamespaces(awsAccountId: string): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/user/${encodeURIComponent(awsAccountId)}/namespaces`,
    });
  }

  validateExistingNamespace(
    teamAccountId: string,
    namespace: string,
  ): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/namespace/check/${teamAccountId}?namespace=${namespace}`,
    });
  }

  triggerSyncJobByRepo(_user: string, repoName: string): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/versioning/sync`,
      body: { repoName },
    });
  }

  getTransitServicesForAccount(accountId: string): Promise<TransitService[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/transit-services/${accountId}`,
    });
  }

  getTransitServicesV2(awsAccountId: string): Promise<string[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/transit-services-v2/${encodeURIComponent(awsAccountId)}`,
    });
  }

  validateSecret(
    body: BlueBoxValidateSecretDto,
  ): Promise<BlueBoxValidateSecretResponseDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/validate-secret`,
      body,
    });
  }

  checkHeliosCoreKmsRole(
    body: HeliosCoreKmsRoleCheckDto,
  ): Promise<HeliosCoreKmsRoleCheckResponseDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/check-helios-core-kms-role`,
      body,
    });
  }

  createAwsAccountAndProject(
    awsAccountProjectDto: AwsAccountProjectDto,
  ): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/account-management/create`,
      body: awsAccountProjectDto,
    });
  }

  getProjectDetails(
    projectId: string,
    bearerToken: string,
  ): Promise<ProjectInfo> {
    return this.submitRequest({
      method: 'GET',
      path: `/account-management/project/${projectId}`,
      bearerToken,
    });
  }

  getAccountRequestsForCurrentUser(
    _currentUser: string,
  ): Promise<AccountRequestDto[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/account-management/account-requests`,
    });
  }

  createAwsAccount(
    awsAccountProjectDto: AwsAccountProjectDto,
    projectId: string,
  ): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/account-management/project/${projectId}/create`,
      body: awsAccountProjectDto,
    });
  }

  getAccountRequestConfig(): Promise<AccountRequestConfig> {
    return this.submitRequest({
      method: 'GET',
      path: `/account-management/account-request-config/all`,
    });
  }

  getRegions(): Promise<Regions> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/regions`,
    });
  }

  getFirewallDomains(region?: string, mbId?: string): Promise<FirewallDomains> {
    let path = `/catalog/firewall-domains`;

    const queryParams = [];
    if (region) {
      queryParams.push(`region=${encodeURIComponent(region)}`);
    }
    if (mbId) {
      queryParams.push(`mbId=${encodeURIComponent(mbId)}`);
    }
    if (queryParams.length) {
      path += `?${queryParams.join('&')}`;
    }

    return this.submitRequest({
      method: 'GET',
      path,
    });
  }

  exportAccountDetails(accountId: string, region: string): Promise<any> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/export/account/${encodeURIComponent(accountId)}?region=${encodeURIComponent(region)}`,
    });
  }

  getDynamicEnumValues(
    awsAccountId: string,
    region?: string,
  ): Promise<string[]> {
    let path = `/catalog/account/dns-prefixes?accountId=${encodeURIComponent(awsAccountId)}`;
    if (region) {
      path += `&region=${encodeURIComponent(region)}`;
    }
    return this.submitRequest({
      method: 'GET',
      path,
    });
  }

  validateMattermostUsername(
    username: string,
  ): Promise<MattermostValidateUsernameDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/mattermost/validate-username/${encodeURIComponent(username)}`,
    });
  }

  syncPlaylists(): Promise<any> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlists/sync`,
    });
  }

  getPlaylists(
    region: string,
    awsAccountId: string,
  ): Promise<CatalogPlaylistDto[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/playlists?region=${encodeURIComponent(region)}&awsAccountId=${encodeURIComponent(awsAccountId)}`,
    });
  }

  getPlaylistById(playlistId: string): Promise<CatalogPlaylistDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/playlists/${encodeURIComponent(playlistId)}`,
    });
  }

  createPlaylistExecution(
    body: CreatePlaylistExecutionDto,
    _userId: string,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions`,
      body,
    });
  }

  getPlaylistExecution(executionId: string): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}`,
    });
  }

  getPlaylistExecutionsByAccount(
    awsAccountId: string,
  ): Promise<PlaylistExecutionDto[]> {
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/playlist-executions?awsAccountId=${encodeURIComponent(awsAccountId)}`,
    });
  }

  savePlaylistExecutionConfig(
    executionId: string,
    body: PlaylistExecutionConfigDto,
    _userId: string,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/config`,
      body,
    });
  }

  triggerPlaylistExecution(
    executionId: string,
    _userId: string,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/trigger`,
    });
  }

  pausePlaylistExecution(
    executionId: string,
    _userId: string,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/pause`,
    });
  }

  resumePlaylistExecution(
    executionId: string,
    _userId: string,
    body?: PlaylistResumeRequestDto,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/resume`,
      body: body ?? {},
    });
  }

  discardPlaylistExecution(
    executionId: string,
    _userId: string,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/discard`,
    });
  }

  uninstallPlaylistExecution(
    executionId: string,
    _userId: string,
    body?: PlaylistUninstallRequestDto,
  ): Promise<PlaylistExecutionDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/playlist-executions/${encodeURIComponent(executionId)}/uninstall`,
      body: body ?? {},
    });
  }

  savePluginConfigDraft(
    body: SavePluginConfigDraftRequestDto,
  ): Promise<PluginConfigDraftResponseDto> {
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/draft`,
      body,
    });
  }

  updatePluginConfigDraft(
    draftId: string,
    body: SavePluginConfigDraftRequestDto,
  ): Promise<PluginConfigDraftResponseDto> {
    return this.submitRequest({
      method: 'PUT',
      path: `/catalog/plugin/draft/${encodeURIComponent(draftId)}`,
      body,
    });
  }

  getPluginConfigDraft(
    awsAccountId: string,
    pluginId: string,
    userId: string,
  ): Promise<PluginConfigDraftResponseDto> {
    const queryParams = [
      `awsAccountId=${encodeURIComponent(awsAccountId)}`,
      `pluginId=${encodeURIComponent(pluginId)}`,
      `userId=${encodeURIComponent(userId)}`,
    ];
    return this.submitRequest({
      method: 'GET',
      path: `/catalog/plugin/draft?${queryParams.join('&')}`,
    });
  }

  installPluginFromDraft(draftId: string, userId: string): Promise<unknown> {
    const query = `?userId=${encodeURIComponent(userId)}`;
    return this.submitRequest({
      method: 'POST',
      path: `/catalog/plugin/draft/${encodeURIComponent(draftId)}/install${query}`,
    });
  }

  deletePluginConfigDraft(draftId: string, userId: string): Promise<unknown> {
    const query = `?userId=${encodeURIComponent(userId)}`;
    return this.submitRequest({
      method: 'DELETE',
      path: `/catalog/plugin/draft/${encodeURIComponent(draftId)}${query}`,
    });
  }

  private async submitRequest({
    path,
    method,
    body,
    responseAsJson = true,
    bearerToken,
  }: SipCentralRequest): Promise<any> {
    const url = `${await this.discoveryApi.getBaseUrl('sip-central')}${path}`;
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      'Bearer-token': bearerToken || '',
    };

    const response = await this.fetchApi.fetch(url, {
      method,
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return responseAsJson ? await response.json() : response;
  }
}
